interface FullscreenElement extends HTMLElement {
  webkitRequestFullScreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface IOSFullscreenVideo extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
  webkitSupportsFullscreen?: boolean;
}

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element;
  mozFullScreenElement?: Element;
  msFullscreenElement?: Element;
  webkitExitFullscreen?: () => Promise<void>;
  webkitCancelFullScreen?: () => Promise<void>;
  mozCancelFullScreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

// iPhone Safari supports the standard Fullscreen API only on the <video>
// element (via webkitEnterFullscreen) — not on arbitrary containers. To make
// the fullscreen button work consistently across video, image, document, and
// text viewers, fall back to a CSS-driven "pseudo-fullscreen" mode when no
// real fullscreen API is available on the element AND no video-native
// fullscreen path is available either.
const PSEUDO_FS_EVENT = 'kollab-pseudo-fullscreenchange';
let pseudoFullscreenElement: HTMLElement | null = null;

// Track the iOS-native fullscreen <video> separately. iOS's
// webkitEnterFullscreen takes over the whole screen (hiding the Safari
// chrome — true fullscreen), but it fires webkitbeginfullscreen /
// webkitendfullscreen instead of the standard fullscreenchange event,
// and document.fullscreenElement stays null throughout. We bridge it so
// getFullscreenElement / addFullscreenChangeListener behave consistently.
let iosFullscreenVideo: IOSFullscreenVideo | null = null;
let iosFullscreenWrapper: HTMLElement | null = null;
let iosEndListener: (() => void) | null = null;

function dispatchPseudoChange(): void {
  document.dispatchEvent(new Event(PSEUDO_FS_EVENT));
}

function elementSupportsRequestFullscreen(element: HTMLElement): boolean {
  const el = element as FullscreenElement;
  return !!(
    el.requestFullscreen ||
    el.webkitRequestFullScreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen
  );
}

function videoSupportsIOSFullscreen(video: HTMLVideoElement | null | undefined): video is IOSFullscreenVideo {
  if (!video) return false;
  const v = video as IOSFullscreenVideo;
  // webkitSupportsFullscreen is true on iOS Safari once metadata is loaded.
  // The method itself is the more reliable feature check across iOS versions.
  return typeof v.webkitEnterFullscreen === 'function';
}

export function requestFullscreen(element: HTMLElement, videoElement?: HTMLVideoElement | null): void {
  // Prefer the real Fullscreen API when the wrapper element supports it
  // (desktop, Android Chrome, iPad with desktop-class Safari).
  const el = element as FullscreenElement;
  if (el.requestFullscreen) {
    el.requestFullscreen();
    return;
  } else if (el.webkitRequestFullScreen) {
    el.webkitRequestFullScreen();
    return;
  } else if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
    return;
  } else if (el.mozRequestFullScreen) {
    el.mozRequestFullScreen();
    return;
  } else if (el.msRequestFullscreen) {
    el.msRequestFullscreen();
    return;
  }

  // iPhone Safari path — the wrapper <div> can't go fullscreen, but the
  // <video> element supports the iOS-native fullscreen video player which
  // *is* true fullscreen (hides Safari's browser chrome). Use it when
  // available so iOS users get a real fullscreen experience instead of
  // the CSS pseudo-fullscreen fallback.
  if (videoSupportsIOSFullscreen(videoElement)) {
    iosFullscreenVideo = videoElement;
    iosFullscreenWrapper = element;
    const endListener = () => {
      iosFullscreenVideo?.removeEventListener('webkitendfullscreen', endListener);
      iosEndListener = null;
      iosFullscreenVideo = null;
      iosFullscreenWrapper = null;
      dispatchPseudoChange();
    };
    iosEndListener = endListener;
    videoElement.addEventListener('webkitendfullscreen', endListener);
    videoElement.webkitEnterFullscreen!();
    dispatchPseudoChange();
    return;
  }

  // No native fullscreen API anywhere — fall back to CSS pseudo-fullscreen.
  // Used for non-video viewers on iPhone (images, documents, text).
  pseudoFullscreenElement = element;
  dispatchPseudoChange();
}

export function exitFullscreen(): void {
  if (iosFullscreenVideo) {
    // iOS may not implement webkitExitFullscreen on older versions, but
    // the user can dismiss it via the native UI. Try the method anyway.
    const video = iosFullscreenVideo;
    try { video.webkitExitFullscreen?.(); } catch { /* ignore */ }
    // If webkitendfullscreen doesn't fire (older iOS, etc.), force-clear.
    if (iosEndListener) {
      video.removeEventListener('webkitendfullscreen', iosEndListener);
      iosEndListener = null;
    }
    iosFullscreenVideo = null;
    iosFullscreenWrapper = null;
    dispatchPseudoChange();
    return;
  }
  if (pseudoFullscreenElement) {
    pseudoFullscreenElement = null;
    dispatchPseudoChange();
    return;
  }
  const doc = document as FullscreenDocument;
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  } else if (doc.webkitCancelFullScreen) {
    doc.webkitCancelFullScreen();
  } else if (doc.mozCancelFullScreen) {
    doc.mozCancelFullScreen();
  } else if (doc.msExitFullscreen) {
    doc.msExitFullscreen();
  }
}

export function getFullscreenElement(): Element | null {
  if (iosFullscreenWrapper) return iosFullscreenWrapper;
  if (pseudoFullscreenElement) return pseudoFullscreenElement;
  const doc = document as FullscreenDocument;
  return (
    doc.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.mozFullScreenElement ??
    doc.msFullscreenElement ??
    null
  );
}

export function isFullscreenSupported(): boolean {
  // Pseudo-fullscreen is always available, so this is effectively true
  // everywhere — but keep the standard-API check for callers that want to
  // gate on native support specifically.
  const doc = document as FullscreenDocument;
  return !!(
    doc.exitFullscreen ||
    doc.webkitExitFullscreen ||
    doc.webkitCancelFullScreen ||
    doc.mozCancelFullScreen ||
    doc.msExitFullscreen
  );
}

export function addFullscreenChangeListener(callback: () => void): () => void {
  const events = [
    'fullscreenchange',
    'webkitfullscreenchange',
    'mozfullscreenchange',
    'MSFullscreenChange',
    PSEUDO_FS_EVENT,
  ];
  events.forEach((e) => document.addEventListener(e, callback));
  return () => {
    events.forEach((e) => document.removeEventListener(e, callback));
  };
}

// Exported for tests.
export function _isPseudoFullscreen(): boolean {
  return pseudoFullscreenElement !== null;
}

export function _isIOSNativeFullscreen(): boolean {
  return iosFullscreenVideo !== null;
}

export function _resetPseudoFullscreen(): void {
  pseudoFullscreenElement = null;
  if (iosFullscreenVideo && iosEndListener) {
    iosFullscreenVideo.removeEventListener('webkitendfullscreen', iosEndListener);
  }
  iosEndListener = null;
  iosFullscreenVideo = null;
  iosFullscreenWrapper = null;
}

// Exported so callers can branch their UI when no native API is available
// on the wrapper (e.g. avoid mounting the fullscreen sidebar on iPhone).
export function isStandardFullscreenAvailable(element: HTMLElement): boolean {
  return elementSupportsRequestFullscreen(element);
}
